import { useCallback, useEffect, useRef, useState } from 'react';
import { getFavouriteContributorsPaginated } from '../services/api';

interface ContributorsInfiniteScrollOptions {
  rootMargin?: string;
  scrollContainerRef?: React.RefObject<HTMLElement | null>;
  enabled?: boolean;
}

interface ContributorsInfiniteScrollState {
  items: any[];
  loading: boolean;
  hasMore: boolean;
  error: string | null;
  total: number;
  loadMore: () => Promise<void>;
  reset: () => void;
  removeItem: (id: string) => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

export const useContributorsInfiniteScroll = ({
  rootMargin = '200px',
  scrollContainerRef,
  enabled = true,
}: ContributorsInfiniteScrollOptions): ContributorsInfiniteScrollState => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef(false);
  const offsetRef = useRef(0);
  const loadMoreRef = useRef<(() => Promise<void>) | null>(null);

  // Reset function to clear data and restart
  const reset = useCallback(() => {
    offsetRef.current = 0;
    setItems([]);
    setHasMore(true);
    setError(null);
    setTotal(0);
    setInitialLoadDone(false);
  }, []);

  // Remove item function
  const removeItem = useCallback((id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    setTotal((prev) => Math.max(0, prev - 1));
  }, []);

  // Load more function
  const loadMore = useCallback(async () => {
    if (loadingRef.current || !hasMore) return;

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await getFavouriteContributorsPaginated(
        12,
        offsetRef.current,
      );

      if (offsetRef.current === 0) {
        // Initial load - replace items
        setItems(result?.items || []);
      } else {
        // Subsequent loads - append items
        setItems((prev) => [...prev, ...(result?.items || [])]);
      }

      const hasNext = result?.pagination?.hasNext || false;
      const totalCount = result?.pagination?.total || 0;

      setHasMore(hasNext);
      setTotal(totalCount);
      offsetRef.current += result?.items?.length || 0;

      // Set initial load done after first successful load
      if (!initialLoadDone) {
        setInitialLoadDone(true);
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to load favorite contributors';
      setError(errorMessage);
      console.error('Error loading favorite contributors:', err);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [hasMore, initialLoadDone]);

  // Update loadMoreRef when loadMore changes
  useEffect(() => {
    loadMoreRef.current = loadMore;
  }, [loadMore]);

  // Reset when enabled changes
  useEffect(() => {
    if (enabled) {
      reset();
    }
  }, [enabled, reset]);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!enabled || !hasMore || !initialLoadDone || !sentinelRef.current)
      return;

    // Disconnect any existing observer first
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          !loadingRef.current &&
          hasMore &&
          loadMoreRef.current
        ) {
          loadMoreRef.current();
        }
      },
      {
        root: scrollContainerRef?.current || null,
        rootMargin,
        threshold: 0.1,
      },
    );

    observerRef.current.observe(sentinelRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [enabled, hasMore, initialLoadDone, rootMargin, scrollContainerRef]);

  // Initial load
  useEffect(() => {
    if (!initialLoadDone && enabled && loadMoreRef.current) {
      setInitialLoadDone(true);
      loadMoreRef.current();
    }
  }, [enabled, initialLoadDone]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    items,
    loading,
    hasMore,
    error,
    total,
    loadMore: loadMoreRef.current || (() => Promise.resolve()),
    reset,
    removeItem,
    sentinelRef,
  };
};

export default useContributorsInfiniteScroll;
